import { useState } from "react";

import type { MentionInputDto } from "@/api/types";

export type PostText = {
  title: string;
  content: string;
  contentTemplate?: string;
  mentions?: MentionInputDto[];
};

type PostCopilotHistory = {
  isUndoable: boolean;
  isRedoable: boolean;
  begin: (post: PostText) => void;
  record: (post: PostText) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
};

/**
 * Tracks one Copilot edit for undo and redo. Call `begin` with the current post before generation, then
 * `record` with the generated post. Undo and redo pass the selected snapshot to `apply` for the caller to write back.
 */
export function usePostCopilotHistory(apply: (post: PostText) => void): PostCopilotHistory {
  const [original, setOriginal] = useState<PostText>();
  const [generated, setGenerated] = useState<PostText>();
  const [isGenerated, setIsGenerated] = useState(false);

  return {
    isUndoable: isGenerated && original !== undefined,
    isRedoable: !isGenerated && generated !== undefined,
    begin(post: PostText) {
      setOriginal(post);
      setGenerated(undefined);
      setIsGenerated(false);
    },
    record(post: PostText) {
      setGenerated(post);
      setIsGenerated(true);
    },
    undo() {
      if (original) {
        apply(original);
        setIsGenerated(false);
      }
    },
    redo() {
      if (generated) {
        apply(generated);
        setIsGenerated(true);
      }
    },
    reset() {
      setOriginal(undefined);
      setGenerated(undefined);
      setIsGenerated(false);
    },
  };
}
